import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParam,
  useQueryParams,
} from "use-query-params";

import { dispatchGetSearchVenues, searchVenueSelector } from "Venues/state";
import { IFilterCategories, ISortingOptionKeys, IVenue } from "Venues/types";
import { IMarker } from "shared/types";
import { isArray, mapValues, reduce } from "lodash-es";
import { useBreakpoint } from "shared/utils";
import { BreakpointSize } from "shared/styles/constants";
import axios, { CancelTokenSource } from "axios";

interface SearchPageContextState {
  showMap: boolean | null | undefined;
  activeId: string | null | undefined;
  activeMarker: IMarker | undefined;
  markers: IMarker[];
  filters: { [key in IFilterCategories]?: any };
  filterCount: number;
  page: number;
  sort: ISortingOptionKeys;
  isMobile: boolean;
  showFilterMobile: boolean | null | undefined;
  toggleShowMap: () => void;
  setActiveId: (id: string) => void;
  clearActiveId: () => void;
  setFilters: (filters: { [category in IFilterCategories]?: any }) => void;
  clearAllFilters: () => void;
  clearFilterCategory: (category: IFilterCategories) => void;
  removeFilterItem: (category: IFilterCategories, value: string) => void;
  handlePageChange: (page: number) => void;
  handleSortChange: (sort: ISortingOptionKeys) => void;
  setShowFilterMobile: (value: boolean) => void;
}

const initialState: SearchPageContextState = {
  showMap: false,
  markers: [],
  activeMarker: undefined,
  filters: {},
  filterCount: 0,
  page: 1,
  sort: ISortingOptionKeys.updatedAtDesc,
  isMobile: false,
  showFilterMobile: false,
  activeId: null,
  toggleShowMap: () => {},
  clearActiveId: () => {},
  setActiveId: () => {},
  setFilters: () => {},
  clearAllFilters: () => {},
  clearFilterCategory: () => {},
  removeFilterItem: () => {},
  handlePageChange: () => {},
  handleSortChange: () => {},
  setShowFilterMobile: () => {},
};

const initialFilters: { [key: string]: any } = {
  [IFilterCategories.title]: undefined,
  [IFilterCategories.country]: [],
  [IFilterCategories.capacity]: [],
  [IFilterCategories.capacityType]: [],
  [IFilterCategories.roomDimension]: [],
  page: undefined,
  sort: undefined,
};

const SearchPageContext = createContext(initialState);

const formatVenueToMarker = ({ address, id }: IVenue): IMarker | undefined => {
  const { longitude, latitude } = address || { longitude: 0, latitude: 0 };
  return {
    id,
    coordinates: { longitude, latitude },
  };
};

export const SearchPageProvider: React.FC = ({ children }) => {
  const [query, setQuery] = useQueryParams({
    [IFilterCategories.title]: StringParam,
    [IFilterCategories.country]: ArrayParam,
    [IFilterCategories.capacity]: ArrayParam,
    [IFilterCategories.capacityType]: ArrayParam,
    [IFilterCategories.roomDimension]: ArrayParam,
    page: NumberParam,
    sort: StringParam,
  });
  const [showMap, setShowMap] = useQueryParam("showMap", BooleanParam);
  const [activeId, setActiveId] = useQueryParam("venue", StringParam);
  const [showFilterMobile, setShowFilterMobile] = useQueryParam(
    "filter",
    BooleanParam
  );
  const venues: IVenue[] = useSelector(searchVenueSelector);
  const isMobile = useBreakpoint(BreakpointSize.l);
  const venueFetchCancelTokenRef = useRef<CancelTokenSource>();

  const dispatch = useDispatch();
  const fetchSearchVenues = useCallback(
    ({
      page,
      sort,
      ...filters
    }: {
      page?: number | null;
      sort?: string | null;
      [key: string]: any;
    }) => {
      const cancelTokenSource = venueFetchCancelTokenRef.current;
      if (cancelTokenSource) {
        // cancel previous request
        cancelTokenSource.cancel();
      }

      // set new cancel token
      const cancelToken = axios.CancelToken.source();
      venueFetchCancelTokenRef.current = cancelToken;
      // scroll to top of page
      window.scrollTo({ top: 0, behavior: "smooth" });

      dispatchGetSearchVenues(
        dispatch,
        {
          filters,
          page: page || undefined,
          sort: (sort as ISortingOptionKeys) || undefined,
        },
        cancelToken?.token
      );
    },
    [dispatch]
  );
  const { page, sort, ...filters } = useMemo(
    () => (query as { [key: string]: any }) || {},
    [query]
  );

  // Refetch venue list when filter params change
  useEffect(() => {
    fetchSearchVenues(query);
  }, [query, fetchSearchVenues]);

  const markers: IMarker[] = useMemo(
    () =>
      venues.reduce((markers: IMarker[], venue) => {
        const marker = formatVenueToMarker(venue);
        if (marker) {
          return [...markers, marker];
        }
        return markers;
      }, []),
    [venues]
  );

  const activeMarker: IMarker | undefined = useMemo(() => {
    const venue = venues.find(({ id }) => id === activeId);
    if (venue) {
      return formatVenueToMarker(venue);
    }
  }, [activeId, venues]);

  const filterCount: number = useMemo(
    () =>
      reduce(
        filters,
        (sum, filter) => {
          if (typeof filter === "string" && filter.length) {
            return sum + 1;
          }

          if (isArray(filter) && filter.length) {
            return sum + filter.length;
          }
          return sum;
        },
        0
      ),
    [filters]
  );

  const clearActiveId = () => {
    setActiveId(undefined);
  };

  const toggleShowMap = () => {
    setShowMap(showMap ? undefined : true);
    setActiveId(undefined);
  };

  const clearAllFilters = () => {
    setQuery(initialFilters);
  };

  const clearFilterCategory = (category: IFilterCategories) => {
    setQuery({
      [category]: initialFilters[category],
      page: undefined,
    });
  };

  const setFilters = (filters: { [category in IFilterCategories]?: any }) => {
    const cleanedFilters = mapValues(filters, (value, category) =>
      !!value && value?.length ? value : initialFilters[category]
    );
    setQuery({
      ...cleanedFilters,
      page: undefined,
    });
  };

  const removeFilterItem = (category: IFilterCategories, value: string) => {
    const oldValues = filters[category];
    const newValues =
      typeof oldValues === "string" || typeof oldValues === "number"
        ? undefined
        : oldValues?.filter((item: string) => item !== value);

    setQuery({
      [category]: newValues?.length ? newValues : initialFilters[category],
      page: undefined,
    });
  };

  const handleSortChange = (sort: ISortingOptionKeys) => {
    setQuery({ sort, page: undefined });
  };

  const handlePageChange = (page: number) => {
    setQuery({ page });
  };

  return (
    <SearchPageContext.Provider
      value={{
        showMap,
        activeId,
        markers,
        activeMarker,
        filters,
        filterCount,
        sort: (sort as ISortingOptionKeys) || ISortingOptionKeys.titleAsc,
        page: page || 1,
        isMobile,
        showFilterMobile,
        clearActiveId,
        setActiveId,
        toggleShowMap,
        setFilters,
        clearAllFilters,
        clearFilterCategory,
        removeFilterItem,
        handlePageChange,
        handleSortChange,
        setShowFilterMobile,
      }}
    >
      {children}
    </SearchPageContext.Provider>
  );
};

export const useSearchPageContext = () => useContext(SearchPageContext);
